import { dataHora, dataSimples } from '../../../lib/datas'
import type { DetalheDaOs } from '../../../types/ordemDeServico'
// Os dados do CONTRATO — quem é a contratada e o CNPJ dela — saem de um lugar só. O termo de
// recebimento já os transcreve do contrato assinado, e uma segunda cópia aqui divergiria da
// primeira no dia em que o contrato for aditado. Quando existir um módulo de contrato, os dois
// documentos passam a lê-lo de lá.
import { TRP } from '../../relatorios/trp/textoDoTrp'
import { OS } from '../textosDaOs'
import { codigoDoDocumento, vigenciaDaOs } from './linhasDaOs'
import { QuadroDeServicos } from './QuadroDeServicos'
import { AssinaturasDaOs, RessalvaDaOs } from './AssinaturasDaOs'
import { DOCUMENTO_DA_OS as T } from './textoDaOs'

/**
 * A ordem de serviço no formato do ANEXO IA/IB, pronta para imprimir.
 *
 * **A repartição é a mesma do TRP** (`DocumentoTrp.tsx` + `textoDoTrp.ts` + `linhasDoTrp.ts`):
 * componente monta, texto fixo transcrito num módulo, linhas formatadas noutro. Não é gosto — é o
 * que permite testar a formatação dos valores sem montar React, e é onde o centavo de maio/2026
 * fica fixado por teste.
 *
 * **Nada é somado aqui.** Os totais chegam do servidor. A prática do contrato arredonda cada linha
 * antes de somar (223.148,15) e arredondar a soma dá outro número (223.148,14) — refazer a conta no
 * navegador imprimiria um terceiro.
 *
 * **A ordem revogada imprime com a revogação em destaque, e não deixa de imprimir.** Ela autorizou
 * despesa antes de ser retirada; uma folha muda sobre isso seria pior que folha nenhuma.
 *
 * @param props a ordem carregada e o instante da emissão da folha
 * @returns o documento
 */
export function DocumentoOs({ ordem, emitidoEm }: { ordem: DetalheDaOs; emitidoEm: string }) {
  const projeto = ordem.regime === 'PROJETO_PONTO_DE_FUNCAO'
  const vigencia = vigenciaDaOs(ordem)
  const codigo = codigoDoDocumento(ordem)

  return (
    <article className="os-documento">
      <section className="os-folha">
        <p className="os-anexo">{projeto ? T.anexoIa : T.anexoIb}</p>
        <h1 className="os-titulo">{projeto ? T.tituloIa : T.tituloIb}</h1>

        {ordem.revogacao && (
          <div className="os-ressalva">
            <p className="os-ressalva-titulo">Ordem de serviço revogada</p>
            {/* `dataHora` e não `dataSimples`: `revogacao.em` é INSTANTE (sufixo Z), e o regex de
                `dataSimples` recorta o AAAA-MM-DD do texto em UTC sem converter fuso. Revogar às
                22h20 de 27/08 (= 2026-08-28T01:20:00Z) imprimia 28/08/2026 na peça que vai ao
                processo, enquanto a tela de detalhe mostrava 27/08 no mesmo instante — dois
                documentos do sistema discordando sobre o dia de um ato. Os campos vizinhos
                (`emitidaEm`, a vigência) SÃO datas puras e continuam com `dataSimples`: ali é o
                regex que está certo, e converter fuso introduziria o defeito espelhado. */}
            <p>
              {ordem.revogacao.motivo} — {OS.revogadaPor(ordem.revogacao.porNome, dataHora(ordem.revogacao.em))}
            </p>
          </div>
        )}

        <h2 className="os-secao">{T.secoes.identificacao}</h2>
        <table className="os-tabela">
          <caption className="sr-only">Identificação da ordem de serviço</caption>
          <tbody>
            <Par rotulo={T.rotulos.numero} valor={ordem.numero} rotulo2={T.rotulos.emissao} valor2={dataSimples(ordem.emitidaEm)} />
            {projeto ? (
              <Par
                rotulo={T.rotulos.tipo}
                valor={ordem.tipo ? OS.tipos[ordem.tipo] : ''}
                rotulo2={T.rotulos.contrato}
                valor2={ordem.contrato}
              />
            ) : (
              <Par
                rotulo={T.rotulos.contrato}
                valor={ordem.contrato}
                rotulo2={T.rotulos.competencia}
                valor2={ordem.competencia ?? ''}
              />
            )}
            <Linha rotulo={T.rotulos.objeto} valor={ordem.objeto} />
            <Par rotulo={T.rotulos.contratado} valor={TRP.contratada} rotulo2={T.rotulos.cnpj} valor2={TRP.cnpjContratada} />
            <Par
              rotulo={T.rotulos.inicioVigencia}
              valor={vigencia.inicio}
              rotulo2={T.rotulos.fimVigencia}
              valor2={vigencia.fim}
            />
            {ordem.numeroExterno && <Linha rotulo={T.rotulos.numeroExterno} valor={ordem.numeroExterno} />}
          </tbody>
        </table>

        <h2 className="os-secao">{T.secoes.requisitante}</h2>
        <table className="os-tabela">
          <caption className="sr-only">Área requisitante</caption>
          <tbody>
            <Linha rotulo={T.rotulos.unidade} valor={ordem.unidade} />
            <Par
              rotulo={T.rotulos.solicitante}
              valor={ordem.solicitanteNome}
              rotulo2={T.rotulos.email}
              valor2={ordem.solicitanteEmail}
            />
          </tbody>
        </table>

        <h2 className="os-secao">{T.secoes.objetivo}</h2>
        <p className="os-paragrafo">{ordem.objetivo}</p>

        <h2 className="os-secao">{T.secoes.servicos}</h2>
        <QuadroDeServicos ordem={ordem} />

        {projeto && ordem.requisitosNaoFuncionais && (
          <>
            <h2 className="os-secao">{T.secoes.requisitos}</h2>
            <p className="os-paragrafo">{ordem.requisitosNaoFuncionais}</p>
          </>
        )}

        {projeto && (ordem.artefatosFornecidos || ordem.artefatosAGerar) && (
          <>
            <h2 className="os-secao">{T.secoes.artefatos}</h2>
            <table className="os-tabela">
              <caption className="sr-only">Artefatos e produtos</caption>
              <tbody>
                <Par
                  rotulo={T.rotulos.fornecidos}
                  valor={ordem.artefatosFornecidos ?? ''}
                  rotulo2={T.rotulos.aGerar}
                  valor2={ordem.artefatosAGerar ?? ''}
                />
              </tbody>
            </table>
          </>
        )}

        <h2 className="os-secao">{T.secoes.assinatura}</h2>
        <p className="os-paragrafo">{T.autorizacao}</p>
        <p className="os-paragrafo">
          {T.local}, {dataSimples(ordem.emitidaEm)}.
        </p>
        <AssinaturasDaOs ordem={ordem} />

        <RessalvaDaOs projeto={projeto} />

        <footer className="os-rodape">
          <span>
            Código do Documento: {codigo} — Impresso em {emitidoEm}
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>Página 1 de 1</span>
        </footer>
      </section>
    </article>
  )
}

/** Uma linha rótulo/valor que ocupa a largura toda. */
function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <tr>
      <th scope="row" style={{ width: '22%' }}>
        {rotulo}
      </th>
      <td colSpan={3}>{valor}</td>
    </tr>
  )
}

/** Dois pares rótulo/valor na mesma linha, como o modelo os dispõe. */
function Par({
  rotulo,
  valor,
  rotulo2,
  valor2,
}: {
  rotulo: string
  valor: string
  rotulo2: string
  valor2: string
}) {
  return (
    <tr>
      <th scope="row" style={{ width: '22%' }}>
        {rotulo}
      </th>
      <td style={{ width: '28%' }}>{valor}</td>
      <th scope="row" style={{ width: '22%' }}>
        {rotulo2}
      </th>
      <td>{valor2}</td>
    </tr>
  )
}
